import { useState } from "react";
import axios from "axios";
import { Download, Mail, MessageCircle, Printer } from "lucide-react";
import { API } from "../lib/apiClient";
import { waTarget } from "../lib/wa";
import { Modal } from "./common";
import { calcCommission, effectiveRate, calcAgentCommission } from "../lib/finance";
import { fmtBirth, ageOf } from "./IdOcrCompare";

const GROUPS = [
  ["Müşteri bilgileri", [["phone", "Telefon"], ["email", "E-posta"], ["cityDistrict", "İl / İlçe"], ["address", "Açık adres"], ["taxNo", "T.C. / Vergi no"], ["birthDate", "Doğum tarihi"]]],
  ["Özet", [["count", "Poliçe sayısı"], ["premium", "Toplam brüt prim"]]],
  ["Poliçe tablosu", [["colPoliceNo", "Poliçe no"], ["colPlate", "Plaka"], ["colCompany", "Şirket"], ["colDates", "Başlangıç / Bitiş"], ["colPremium", "Prim"]]],
  ["Hak edişler (sadece kendi kullanımınız için)", [["personal", "Kişisel hak ediş"], ["agent", "Acente hak edişi"]]],
];
const ALL = GROUPS.flatMap(([, f]) => f.map(([k]) => k));
const EARN = ["personal", "agent"];
const PRESETS = {
  full: Object.fromEntries(ALL.map((k) => [k, !EARN.includes(k)])),
  customer: Object.fromEntries(ALL.map((k) => [k, !EARN.includes(k) && k !== "taxNo" && k !== "birthDate"])),
};
const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch]));
const tl = (n) => Number(n || 0).toLocaleString("tr-TR", { style: "currency", currency: "TRY" });

function buildData(c, rows, o, branches, settings) {
  const rate = Number(c.commissionRate) || 0;
  const calc = rows.map((p) => ({
    p,
    personal: calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null).personal,
    agent: rate > 0 ? calcAgentCommission(p, [c], branches, settings).agent : 0,
  }));
  const boxes = [
    o.phone && ["Telefon", c.phone], o.email && ["E-posta", c.email],
    o.cityDistrict && ["İl / İlçe", [c.city, c.district].filter(Boolean).join(" / ")], o.address && ["Açık adres", c.address],
    o.taxNo && ["T.C. / Vergi no", c.taxNo], o.birthDate && ["Doğum tarihi", c.birthDate ? `${fmtBirth(c.birthDate)} (${ageOf(c.birthDate)} yaş)` : ""],
    o.count && ["Poliçe sayısı", String(rows.length)], o.premium && ["Toplam brüt prim", tl(rows.reduce((s, p) => s + Number(p.premium || 0), 0))],
    o.personal && ["Toplam kişisel hak ediş", tl(calc.reduce((s, x) => s + x.personal, 0))],
    o.agent && rate > 0 && [`Acente hak edişi (%${rate})`, tl(calc.reduce((s, x) => s + x.agent, 0))],
  ].filter(Boolean);
  const cols = [
    o.colPoliceNo && ["POLİÇE NO", (x) => x.p.policeNo], ["BRANŞ", (x) => x.p.branch], o.colPlate && ["PLAKA", (x) => x.p.plate],
    o.colCompany && ["ŞİRKET", (x) => x.p.company], o.colDates && ["BAŞLANGIÇ", (x) => x.p.start], o.colDates && ["BİTİŞ", (x) => x.p.end],
    o.colPremium && ["BRÜT PRİM", (x) => tl(x.p.premium)], o.personal && ["KİŞİSEL", (x) => tl(x.personal)],
    o.agent && rate > 0 && ["ACENTE", (x) => tl(x.agent)],
  ].filter(Boolean);
  return { boxes, cols, calc };
}

function buildHtml(c, rows, o, branches, settings) {
  const { boxes, cols, calc } = buildData(c, rows, o, branches, settings);
  return `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${esc(c.name)} — Müşteri Özeti</title><style>
    *{box-sizing:border-box}body{font:14px/1.5 -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#0f172a;padding:30px;max-width:780px;margin:0 auto}
    h1{font-size:22px;margin:0 0 4px}.sub{color:#64748b;font-size:13px;margin-bottom:20px}
    .grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:16px 0 24px}
    .box{border:1px solid #e2e8f0;border-radius:8px;padding:12px}
    .box small{color:#64748b;font-size:11px;letter-spacing:.3px;text-transform:uppercase}.box strong{display:block;font-size:16px;margin-top:4px}
    table{width:100%;border-collapse:collapse;margin-top:10px;font-size:12px}th,td{text-align:left;padding:8px;border-bottom:1px solid #e2e8f0}th{background:#f8fafc;font-size:10px;letter-spacing:.5px;color:#64748b}
    footer{margin-top:30px;color:#94a3b8;font-size:11px;text-align:center;border-top:1px solid #e2e8f0;padding-top:12px}
    @media print{body{padding:15px}@page{margin:1cm}}
  </style></head><body>
    <h1>${esc(c.name)}</h1><div class="sub">${esc(c.type)}</div>
    ${boxes.length ? `<div class="grid">${boxes.map(([l, v]) => `<div class="box"><small>${esc(l)}</small><strong>${esc(v || "-")}</strong></div>`).join("")}</div>` : ""}
    <h3 style="font-size:14px;margin:20px 0 6px">Poliçeler</h3>
    <table><thead><tr>${cols.map(([h]) => `<th>${h}</th>`).join("")}</tr></thead><tbody>
      ${calc.map((x) => `<tr>${cols.map(([, f]) => `<td>${esc(f(x) || "-")}</td>`).join("")}</tr>`).join("")}
    </tbody></table>
    <footer>SigortaCrm · Yazdırıldı ${new Date().toLocaleString("tr-TR")}</footer>
    <script>window.onload=()=>setTimeout(()=>window.print(),300)</script>
  </body></html>`;
}

export function PrintOptionsModal({ customer, policies, branches, settings, onClose, onDocCreated }) {
  const [o, setO] = useState(PRESETS.full);
  const [busy, setBusy] = useState("");
  const rows = policies.filter((p) => p.customer === customer.name);
  const hasRate = Number(customer.commissionRate) > 0;
  const print = () => {
    const w = window.open("", "_blank", "width=800,height=1000");
    if (!w) return;
    w.document.write(buildHtml(customer, rows, o, branches, settings));
    w.document.close();
    onClose();
  };
  const payload = (save) => {
    const { boxes, cols, calc } = buildData(customer, rows, o, branches, settings);
    return { save, boxes: boxes.map(([l, v]) => [l, v || "-"]), columns: cols.map(([h]) => h), rows: calc.map((x) => cols.map(([, f]) => String(f(x) || "-"))) };
  };
  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { window.alert(e?.response?.data?.detail || "İşlem başarısız"); } finally { setBusy(""); }
  };
  const savePdf = async () => {
    const { data } = await axios.post(`${API}/customers/${customer.id}/summary-pdf`, payload(true));
    onDocCreated?.(data);
    return data;
  };
  const download = () => run("pdf", async () => {
    const { data, headers } = await axios.post(`${API}/customers/${customer.id}/summary-pdf`, payload(false), { responseType: "blob" });
    const m = /filename\*=UTF-8''([^;]+)/.exec(headers["content-disposition"] || "");
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = m ? decodeURIComponent(m[1]) : "musteri-ozeti.pdf";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  });
  const whatsapp = () => {
    const w = window.open("", "_blank");
    run("wa", async () => {
      try {
        const doc = await savePdf();
        const { data } = await axios.post(`${API}/customer-documents/${doc.id}/share-link`);
        const text = `Merhaba ${customer.name}, müşteri özetiniz: ${process.env.REACT_APP_BACKEND_URL}${data.path} (bağlantı 7 gün geçerlidir)`;
        const url = `https://wa.me/${waTarget(customer.phone)}?text=${encodeURIComponent(text)}`;
        if (w) w.location.href = url; else window.location.href = url;
        onClose();
      } catch (e) { w?.close(); throw e; }
    });
  };
  const email = () => {
    const to = window.prompt("Müşteri özeti hangi adrese gönderilsin?", customer.email || "");
    if (!to) return;
    run("mail", async () => {
      const doc = await savePdf();
      await axios.post(`${API}/customer-documents/${doc.id}/email`, { to: to.trim() });
      window.alert(`Müşteri özeti ${to.trim()} adresine gönderildi`);
      onClose();
    });
  };
  return (
    <Modal title="Yazdırma seçenekleri" onClose={onClose}>
      <div className="print-opts" data-testid="print-options">
        <div className="print-presets">
          <span>Hızlı seçim:</span>
          <button type="button" className="ghost-button" onClick={() => setO(PRESETS.customer)} data-testid="print-preset-customer">Müşteri için</button>
          <button type="button" className="ghost-button" onClick={() => setO(PRESETS.full)} data-testid="print-preset-full">Tam</button>
        </div>
        {GROUPS.map(([title, fields]) => (
          <fieldset key={title} className={title.startsWith("Hak") ? "earn" : ""}>
            <legend>{title}</legend>
            {fields.map(([k, l]) => {
              const disabled = k === "agent" && !hasRate;
              return (
                <label key={k} className={disabled ? "disabled" : ""}>
                  <input type="checkbox" checked={!!o[k] && !disabled} disabled={disabled} onChange={(e) => setO((x) => ({ ...x, [k]: e.target.checked }))} data-testid={`print-opt-${k}`} />
                  {l}{disabled ? " (komisyon oranı yok)" : ""}
                </label>
              );
            })}
          </fieldset>
        ))}
        <div className="print-actions">
          <button type="button" className="outline-button" onClick={download} disabled={!!busy} data-testid="print-pdf-download"><Download size={14} /> {busy === "pdf" ? "Hazırlanıyor…" : "PDF indir"}</button>
          <button type="button" className="outline-button wa" onClick={whatsapp} disabled={!!busy} data-testid="print-pdf-whatsapp"><MessageCircle size={14} /> {busy === "wa" ? "Hazırlanıyor…" : "WhatsApp ile gönder"}</button>
          <button type="button" className="outline-button" onClick={email} disabled={!!busy} data-testid="print-pdf-email"><Mail size={14} /> {busy === "mail" ? "Gönderiliyor…" : "E-posta ile gönder"}</button>
          <button type="button" className="primary-button" onClick={print} disabled={!!busy} data-testid="print-options-submit"><Printer size={14} /> Yazdır</button>
        </div>
        <small className="print-note">WhatsApp ve e-posta ile gönderilen PDF, müşterinin Belge arşivine "Diğer" olarak kaydedilir.</small>
      </div>
    </Modal>
  );
}
